declare var $: any;
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { EndPointService } from '@shared/services';
import { LeftBarService } from './../core/left-bar/left-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { BlockAppDialogComponent } from '../shared/block-app-dialog/block-app-dialog.component';
import { DeviceHelperService } from '@shared/services';
import { DevicePlatform } from '@shared/common';
import packageJson from '@workspace/package.json';
import { ENVIRONMENT, Environment } from '@shared/environment';
import { StorageHelperService } from '@shared/services';

export enum ConnectionState {
	error,
	disconnected,
	connected,
	connecting,
}
@Injectable({
	providedIn: 'root',
})
export class NotificationGlobalService {
	private readonly document = inject(DOCUMENT);
	private readonly environment = inject<Environment>(ENVIRONMENT);
	private connection;
	private proxy: any;
	signalrUrl = this.environment.env.apiUrl;
	version = packageJson.mobileVer;
	connectionStatus = ConnectionState.disconnected;
	companyId;
	userId;
	private reconnectAttempts = 0;
	private readonly maxReconnectAttempts = 2;

	// #2388
	// get notification count
	receiveUnseenNotificationsCountSource = new BehaviorSubject<number>(0);
	receiveUnseenNotificationsCount$ =
		this.receiveUnseenNotificationsCountSource.asObservable();
	// receive notification for desktop
	receiveInternalNotificationSource = new Subject<any>();
	receiveInternalNotification$ =
		this.receiveInternalNotificationSource.asObservable();

	constructor(
		private toastrService: ToastrService,
		private endPointService: EndPointService,
		private leftBarService: LeftBarService,
		private http: HttpClient,
		private dialog: MatDialog,
		private deviceHelper: DeviceHelperService,
		private storageHelper: StorageHelperService
	) {
		this.endPointService.companySource.subscribe(data => {
			this.signalrUrl = data.apiUrl;
		});
	}

	startReconnectChecker() {
		switch (this.connectionStatus) {
			case ConnectionState.error:
			case ConnectionState.disconnected:
				if (this.reconnectAttempts < this.maxReconnectAttempts) {
					this.reconnectAttempts++;
					this.startConnection();
				} else {
					console.error(
						`[NotificationHub] Max reconnect attempts (${this.maxReconnectAttempts}) reached.`
					);
				}
				break;
			case ConnectionState.connected:
			case ConnectionState.connecting:
				break;
		}
	}

	setParams(companyId, userId) {
		this.companyId = companyId;
		this.userId = userId;
	}

	checkAppVersion() {
		return this.http.post('/appversion/checkAppVersion/', {
			AppVersion: this.version,
		});
	}
	async startConnection(): Promise<void> {
		this.updateConnectionStatus(ConnectionState.connecting);
		if (this.connection) {
			delete this.connection;
		}

		const token = await this.storageHelper.getItem('token');
		const companyCode = await this.storageHelper.getItem('companyCode');

		this.connection = $.hubConnection(this.signalrUrl);
		this.connection.qs = {
			tokenCC: () => companyCode ?? '',
			access_token: () => {
				if (!token) return '';
				return token.toLowerCase().startsWith('bearer ')
					? token.substring(7)
					: token;
			},
		};
		this.proxy = this.connection.createHubProxy('notificationHub');

		this.proxy.on('sendToAll', (latestMsg: string) =>
			this.onNotificationReceived(latestMsg)
		);
		this.proxy.on('onConnected', (data: unknown) => this.onConnected(data));

		this.connection.disconnected(() => {
			this.updateConnectionStatus(ConnectionState.disconnected);
		});

		this.proxy.on('ReceiveUnseenNotificationsCount', (count: number) => {
			this.receiveUnseenNotificationsCountSource.next(count);
		});
		this.proxy.on(
			'ReceiveInternalNotification',
			(notification: unknown) => {
				this.receiveInternalNotificationSource.next(notification);
			}
		);

		this.proxy.on('notificationAppVersion', (versionServer: string) =>
			this.checkVersion(versionServer)
		);

		this.connection
			.start()
			.done((data: unknown) => {
				this.updateConnectionStatus(ConnectionState.connected);
				this.reconnectAttempts = 0;
				this.connect();
			})
			.catch((error: unknown) => {
				console.error('[NotificationHub] Connection failed:', error);
				this.updateConnectionStatus(ConnectionState.error);
				this.startReconnectChecker();
			});

		this.connection.reconnecting(() => {
			this.updateConnectionStatus(ConnectionState.connecting);
		});

		this.connection.reconnected(() => {
			this.updateConnectionStatus(ConnectionState.connected);
		});
	}

	updateConnectionStatus(status) {
		// console.log('udpate to status: ' + status);
		this.connectionStatus = status;

		// log for check notification connection
		const statusEl = this.document.getElementById('notif-status');
		if (statusEl) {
			statusEl.textContent = this.getConnectionStatus();
		}
	}

	getConnectionStatus() {
		return ConnectionState[this.connectionStatus];
	}

	checkVersion(versionServer: string) {
		const platform = this.deviceHelper.getDevicePlatform();
		if (
			platform === DevicePlatform.android ||
			platform === DevicePlatform.ios
		) {
			if (this.version !== versionServer) {
				this.dialog.open(BlockAppDialogComponent, {
					disableClose: true,
					width: '250px',
					data: {
						title: 'Update Required',
						body: 'Please install the latest mobile version',
					},
				});
			}
		}
	}

	connect() {
		this.proxy.invoke('Connect', this.companyId, this.userId);
	}

	chatConnect() {
		this.proxy.invoke('Connect', this.companyId, this.userId);
	}

	onConnected(data) {}

	onNotificationReceived(latestMsg) {
		this.toastrService.success(latestMsg);
	}

	pushNotification(notification) {
		const audio = new Audio('../../assets/unconvinced.mp3');
		if (notification.checkAudio) {
			audio.play();
		}
		this.leftBarService.leftMenuReload('');
		this.toastrService.success(
			notification.notiMessage.Notification_Content,
			notification.notiMessage.Notification_Title
		);
	}

	// countNotificationUnreadNotBySelf() {

	//     this.proxy.invoke('notificationUnreadNotBySelf', this.companyId, this.userId);
	// }
}
